import InfoContent from '@components/infoSection/InfoContent'
import BasicLayout from '@components/layout/BasicLayout'
import FullScreenSection from '@components/layout/FullScreenSection'
import ScrollInAnimation from '@components/scrollUpEffect/scrollUpEffect'
import StarSystemButtonPage from '@components/StarSystemButtonPage/buttonPage'
import getDynasties from '@operations/queries/getDynasties'
import getPage from '@operations/queries/getPage'
import getSiteSettings from '@operations/queries/getSiteSettings'
import useMediaQuery, { maxWidthQueries, minWidthQueries } from '@operations/utils/useMediaQuery'
import Link from 'next/link'
import React from 'react'
import styled from 'styled-components'

const StarBackground = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100%;
  background: url(/images/starSystem/star_system_background.png) center center;
  background-repeat: no-repeat;
  background-size: cover;
  overflow: hidden;
  @media ${maxWidthQueries.sm} {
    flex-direction: column;
    height: auto;
  }
`
const StyledPlanet = styled.img`
  position: absolute;
  z-index: 5;
  ${({ width }) => width && `width: ${width}`};
  max-width: 100%;
  animation: rotateTheRing infinite 1500s linear;
  @keyframes rotateTheRing {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`

const Outline = styled.img`
  position: relative;
  z-index: 100;
  ${({ width }) => width && `width: ${width}`};
  border-radius: 50%;
  &:hover {
    animation: rotateTheRing infinite 7s linear;
    border-radius: 50%;
    z-index: 100;
  }
  @keyframes rotateTheRing {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`
const PlanetWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: ${({ width }) => width || '50%'};
  ${({ margin }) => margin && `margin: ${margin}`};

  @media ${minWidthQueries.md} {
    animation: scale 7s linear;
    @keyframes scale {
      0% {
        transform: scale(0.1);
      }
    }
  }
  &:after {
    content: '';
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    width: ${({ outlineWidth }) => outlineWidth || '50%'};
    height: 100%;
  }
  &:hover {
    &:after {
      background: radial-gradient(circle at 50px 100px, rgba(250, 250, 250, 0.2), rgb(0, 0, 0))
        center 95%;
    }
  }
`

const AnimatedPlanets = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  @media ${minWidthQueries.md} {
    flex-direction: row;
    width: 100%;
    position: absolute;
    left: 0;

    animation: rotPlanets 7s linear;
    animation-timing-function: cubic-bezier(0.2, 0.6, 0.6, 1);
    @keyframes rotPlanets {
      0% {
        position: absolute;
        left: -100%;
        transform: rotate(-40deg);
      }
      100% {
        position: absolute;
        left: 0;
        transform: rotate(0deg);
      }
    }
  }
`
const PopupBox = styled.div`
  @media ${minWidthQueries.md} {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1000px;
    max-width: 100%;

    }
  }
`

const MobilePlanet = styled.div`
  @media ${maxWidthQueries.sm} {
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 100px;
  }
`

const World = ({ data: { siteSettings, dynastiesList } }) => {
  const isMobile = useMediaQuery(maxWidthQueries.sm)

  return (
    <BasicLayout
      backgroundColor="#000"
      dynastiesList={dynastiesList}
      footerLinks={siteSettings?.footerLinks}
      footerLogo={siteSettings?.footerLogo?.url}
      headerLinks={siteSettings?.headerLinks}
      headerLogo={siteSettings?.logo?.url}
      socialLinks={siteSettings?.socialLinks}
    >
      <FullScreenSection
        maxHeight={isMobile ? '88vh ' : '100vh'}
        padding={isMobile ? '1 0 0' : '0'}
      >
        <StarBackground>
          <StarSystemButtonPage>
            <ScrollInAnimation>
              <PopupBox>
                <InfoContent>
                  In a distant star system,
                  <br />
                  two ancient factions wage eternal war.
                  <br />
                  Three known Dynasties caught in the conflict...
                  <br />
                </InfoContent>
              </PopupBox>
            </ScrollInAnimation>
            <ScrollInAnimation left={0} />
            <AnimatedPlanets>
              <MobilePlanet>
                <Link href="/world/hex">
                  <PlanetWrap
                    margin={isMobile ? '2rem 0 2rem' : ''}
                    outlineWidth="60%"
                    width={isMobile ? '80%' : '100%'}
                  >
                    <Outline
                      alt="Planet Background"
                      src="/images/starSystem/planetaryOutline.png"
                      width="60%"
                    />
                    <StyledPlanet
                      alt="Hex"
                      src="images/starSystem/hex_transparent.png"
                      width="50%"
                    />
                  </PlanetWrap>
                </Link>
              </MobilePlanet>

              <MobilePlanet>
                <Link href="/world/lithan">
                  <PlanetWrap outlineWidth="80%" width={isMobile ? '100%' : '100%'}>
                    <Outline
                      alt="Planet Background"
                      src="/images/starSystem/planetaryOutline.png"
                      width="80%"
                    />
                    <StyledPlanet alt="Lithan" src="images/starSystem/Lithan.png" width="80%" />
                  </PlanetWrap>
                </Link>
              </MobilePlanet>
            </AnimatedPlanets>
          </StarSystemButtonPage>
        </StarBackground>
      </FullScreenSection>
    </BasicLayout>
  )
}

export const getStaticProps = async () => {
  let siteSettings
  let dynastiesList = []
  let page
  if (process.env.API_URL) {
    siteSettings = await getSiteSettings()
    page = await getPage('world')
    dynastiesList = await getDynasties()
  }

  return {
    props: {
      data: {
        dynastiesList: dynastiesList || null,
        page: page?.[0] || null,
        siteSettings: siteSettings || null,
      },
    },
  }
}
export default World
